import { useMutation } from "@tanstack/react-query";
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api-client";
import { toast } from "react-toastify";

const registerUser = async ({ data, navigate, locationState }) => {
    const auth = getAuth();
    let isResuming = false;

    try {
        await createUserWithEmailAndPassword(auth, data.email, data.password);
    } catch (err) {
        if (err.code === "auth/email-already-in-use") {
            // Could be an existing account or a registration from a failed attempt
            try {
                await signInWithEmailAndPassword(
                    auth,
                    data.email,
                    data.password,
                );
                isResuming = true;
            } catch (err) {
                // Password didn't match
                toast.error(
                    "An account with this email already exists. Try logging in instead.",
                );
                throw err;
            }
        } else if (err.code === "auth/password-does-not-meet-requirements") {
            toast.error("Please choose a stronger password and try again.");
            throw err;
        } else if (err.code === "auth/invalid-email") {
            toast.error("Please check your email address and try again.");
            throw err;
        } else {
            toast.error("Something went wrong. Please try again.");
            Sentry.captureException(err);
            throw err;
        }
    }

    if (isResuming) {
        try {
            const response = await api.get(`/Account/${data.claimType}/status`);

            if (response.data) {
                // Existing user so stop here
                toast.error(
                    "An account with this email already exists. Try logging in instead.",
                );
                signOut(auth);
                return;
            }
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
            Sentry.captureException(err);
            throw err;
        }
    }

    try {
        await api.post(`/Account/${data.claimType}`, {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
        });
    } catch (err) {
        // The Firebase account exists, but the profile wasn't created in db
        toast.error(
            "Your account was created but setup didn't finish. Please try registering again.",
        );
        Sentry.captureException(err, {
            tags: {
                action: data.claimType
                    ? `${data.claimType}_registration`
                    : "unknown_registration",
            },
        });
        throw err;
    }

    navigate("/verify-email", { state: locationState });
};

export const useRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();

    return useMutation({
        mutationFn: (data) =>
            registerUser({ data, navigate, locationState: location.state }),
        retry: false,
    });
};
